'use client';

import { AiVehicleAnalysis } from '@/components/AiVehicleAnalysis';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MIXPANEL_EVENTS, useMixpanelEvent } from '@/lib/mixpanel';
import {
  aiAnalysisStore,
  selectAiAnalysisIsLoadingAnalysis,
  selectAiAnalysisShowAnalysis,
  useAiAnalysisStore,
} from '@/stores/aiAnalysisStore';
import { leadGenInfoStore } from '@/stores/leadGenInfoStore';
import {
  searchRequestStore,
  selectSearchRequest,
  selectVehicleOptions,
  useSearchRequestStore,
} from '@/stores/searchRequestStore';
import { Bot, LoaderCircle } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { equalityFn } from 'wired-store';
import { SEARCH_TYPES, SearchRequest } from '~/dtos/plan-search';
import { getUrlParams } from '~/modules/utilities/misc_utils';

interface VehicleSearchFormProps {
  variant?: 'homepage' | 'plans';
  initialStates?: string[] | null;
  initialVehicleOptions?: VehicleOptions | null;
}

type VehicleOptions = Record<string, Record<string, string[]>>;

export function VehicleSearchForm({
  variant = 'homepage',
  initialStates = null,
  initialVehicleOptions = null,
}: VehicleSearchFormProps) {
  const isHomepage = variant === 'homepage'; //Don't copy this pattern

  const router = useRouter();
  const [states, setStates] = useState<string[]>(initialStates ?? []);
  const [hasLoadedFreshVehicleOptions, setHasLoadedFreshVehicleOptions] = useState(false);

  const track = useMixpanelEvent();

  const { isLoading: isAiAnalysisLoading, showAnalysis } = useAiAnalysisStore(
    (state) => ({
      isLoading: selectAiAnalysisIsLoadingAnalysis(state),
      showAnalysis: selectAiAnalysisShowAnalysis(state),
    }),
    equalityFn,
  );

  const { searchRequest, vehicleOptions } = useSearchRequestStore(
    (state) => ({
      searchRequest: selectSearchRequest(state),
      vehicleOptions: selectVehicleOptions(state),
    }),
    equalityFn,
  );

  const fetchValidStates = async () => {
    try {
      const response = await fetch('/api/states');
      const data = await response.json();
      setStates(data);
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  };

  useEffect(() => {
    let isMounted = true;

    fetchValidStates();
    searchRequestStore.reset();
    if (initialVehicleOptions) {
      searchRequestStore.produce(function seedInitialVehicleOptions(draft) {
        draft.allVehicleOptions = initialVehicleOptions;
      });
    }
    aiAnalysisStore.reset();
    searchRequestStore.getVehicleOptions().then((freshVehicleOptions) => {
      if (isMounted && freshVehicleOptions) {
        const { year, make, model } = selectSearchRequest();
        const selectedYearOptions = year ? freshVehicleOptions[year] : null;
        const selectedModelOptions = selectedYearOptions && make ? selectedYearOptions[make] : null;

        if (year && !selectedYearOptions) {
          searchRequestStore.changeSearchRequest({ year: '', make: '', model: '' });
        } else if (make && !selectedModelOptions) {
          searchRequestStore.changeSearchRequest({ make: '', model: '' });
        } else if (model && !selectedModelOptions?.includes(model)) {
          searchRequestStore.changeSearchRequest({ model: '' });
        }

        setHasLoadedFreshVehicleOptions(true);
      }
    });
    leadGenInfoStore.reset();

    return () => {
      isMounted = false;
    };
  }, [initialVehicleOptions]);

  const getMyPlansClick = (requestOverrides: Partial<SearchRequest> = {}) => {
    const queryParams = getUrlParams({ ...searchRequest, ...requestOverrides });
    const queryString = queryParams ? `?${queryParams}` : ``;
    router.push(`/plans${queryString}`);
    track(MIXPANEL_EVENTS.HOME_PAGE_SEARCH, {
      searchRequest: searchRequest,
    });
  };

  const currentVehicleOptions = (vehicleOptions as VehicleOptions | null) ?? initialVehicleOptions;
  const yearOptions = currentVehicleOptions
    ? Object.keys(currentVehicleOptions).sort((a, b) => b.localeCompare(a))
    : [];
  const makeOptions =
    currentVehicleOptions && searchRequest.year
      ? Object.keys(currentVehicleOptions[searchRequest.year] ?? {}).sort()
      : [];
  const modelOptions =
    currentVehicleOptions && searchRequest.year && searchRequest.make
      ? (currentVehicleOptions[searchRequest.year]?.[searchRequest.make] ?? [])
      : [];
  const isVehicleOptionsLoading = !currentVehicleOptions;
  const isMakeModelLoading = isVehicleOptionsLoading || isAiAnalysisLoading;
  const hasSelectedYear = Boolean(searchRequest.year);
  const hasSelectedMake = Boolean(searchRequest.make);
  const isMakeDisabled = isMakeModelLoading || !hasSelectedYear;
  const isModelDisabled = isMakeModelLoading || !hasSelectedYear || !hasSelectedMake;
  const isSubmitWaitingForFreshVehicleOptions = !hasLoadedFreshVehicleOptions;
  const isMakeModelComplete = Boolean(
    searchRequest.year && searchRequest.make && searchRequest.model && searchRequest.state && searchRequest.mileage,
  );
  const inputSurfaceClass = `${isHomepage ? 'bg-gray-50' : 'bg-white'} text-gray-900 transition-all duration-200 focus-visible:ring-[#00B67A]/60 disabled:bg-gray-100 disabled:text-gray-400`;
  const reactivePanelClass = 'rounded-xl transition-all duration-300';
  const makeModelPanelClass = isMakeModelLoading ? 'opacity-75 saturate-75' : 'opacity-100';
  const alternateLookupPanelClass = isAiAnalysisLoading ? 'opacity-75 saturate-75' : 'opacity-100';
  const completedFieldClass = 'border-[#00B67A]/50 shadow-[0_0_0_1px_rgba(0,182,122,0.12)]';
  const renderFreshVehicleOptionsHint = (showHint: boolean) =>
    showHint ? (
      <div className="flex items-center gap-1.5 text-[11px] leading-none text-gray-500 md:mr-auto">
        <LoaderCircle className="icon-spin h-3 w-3 stroke-[#00B67A]" strokeWidth={2.5} />
        <span>Loading latest vehicle data...</span>
      </div>
    ) : null;

  return (
    <div id="vehicleSearchForm" className={`w-full max-w-4xl mx-auto bg-transparent ${isHomepage ? '' : ''}`}>
      <Tabs
        value={searchRequest.searchType}
        onValueChange={(e) => {
          searchRequestStore.changeSearchRequest({ searchType: e });
        }}
        className="w-full"
      >
        <TabsList className="w-full flex justify-start bg-transparent border-0">
          <TabsTrigger
            value={SEARCH_TYPES.MAKEMODEL}
            disabled={isAiAnalysisLoading}
            className="px-6 py-3 text-sm font-medium text-gray-500 border-b-2 border-transparent data-[state=active]:border-[#00B67A] data-[state=active]:text-[#00B67A] rounded-none bg-transparent data-[state=active]:bg-transparent hover:text-gray-700 search-form-tab"
          >
            Make and Model
          </TabsTrigger>

          <TabsTrigger
            value={SEARCH_TYPES.VIN}
            disabled={isAiAnalysisLoading}
            className="px-6 py-3 text-sm font-medium text-gray-500 border-b-2 border-transparent data-[state=active]:border-[#00B67A] data-[state=active]:text-[#00B67A] rounded-none bg-transparent data-[state=active]:bg-transparent hover:text-gray-700 search-form-tab"
          >
            VIN
          </TabsTrigger>

          <TabsTrigger
            value={SEARCH_TYPES.LICENSEPLATE}
            disabled={isAiAnalysisLoading}
            className="px-6 py-3 text-sm font-medium text-gray-500 border-b-2 border-transparent data-[state=active]:border-[#00B67A] data-[state=active]:text-[#00B67A] rounded-none bg-transparent data-[state=active]:bg-transparent hover:text-gray-700 search-form-tab"
          >
            License Plate
          </TabsTrigger>
        </TabsList>

        <div className={`py-6 px-0 ${reactivePanelClass}`}>
          {/* MAKE MODEL SEARCH */}
          <TabsContent
            value={SEARCH_TYPES.MAKEMODEL}
            className={`m-0 space-y-4 data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:slide-in-from-bottom-1 ${makeModelPanelClass}`}
          >
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label htmlFor="year" className="text-sm text-gray-600 mb-1.5 block">
                  Year
                </label>
                <Select
                  value={searchRequest.year ?? ''}
                  disabled={isMakeModelLoading}
                  onValueChange={(e) => {
                    searchRequestStore.changeSearchRequest({ year: e });
                  }}
                >
                  <SelectTrigger
                    id="year"
                    className={`${inputSurfaceClass} ${searchRequest.year ? completedFieldClass : ''}`}
                  >
                    <div className="flex justify-end items-center">
                      <SelectValue placeholder="Year" />
                    </div>

                    {isVehicleOptionsLoading ? (
                      <div className="flex justify-end items-center w-full px-2">
                        <LoaderCircle
                          className="icon-spin stroke-green-700 "
                          style={{ width: '30px', height: '30px' }}
                          strokeWidth={2}
                        />
                      </div>
                    ) : null}
                  </SelectTrigger>
                  <SelectContent>
                    {vehicleOptions &&
                      yearOptions.map((year) => (
                        <SelectItem key={year} value={year}>
                          {year}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label htmlFor="make" className="text-sm text-gray-600 mb-1.5 block">
                  Make
                </label>
                <Select
                  value={searchRequest.make ?? ''}
                  disabled={isMakeDisabled}
                  onValueChange={(e) => {
                    searchRequestStore.changeSearchRequest({
                      make: e,
                      model: '',
                    });
                  }}
                >
                  <SelectTrigger
                    id="make"
                    className={`${inputSurfaceClass} ${searchRequest.make ? completedFieldClass : ''}`}
                  >
                    <SelectValue placeholder={hasSelectedYear ? 'Make' : 'Select year first'} />
                  </SelectTrigger>
                  <SelectContent>
                    {vehicleOptions &&
                      searchRequest.year &&
                      makeOptions.map((make: any) => (
                        <SelectItem key={make} value={make}>
                          {make}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label htmlFor="model" className="text-sm text-gray-600 mb-1.5 block">
                  Model
                </label>
                <Select
                  value={searchRequest.model ?? ''}
                  disabled={isModelDisabled}
                  onValueChange={(e) => {
                    searchRequestStore.changeSearchRequest({ model: e });
                  }}
                >
                  <SelectTrigger
                    id="model"
                    className={`${inputSurfaceClass} ${searchRequest.model ? completedFieldClass : ''}`}
                  >
                    <SelectValue
                      placeholder={
                        hasSelectedMake ? 'Model' : hasSelectedYear ? 'Select make first' : 'Select year first'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {vehicleOptions &&
                      searchRequest.year &&
                      searchRequest.make &&
                      modelOptions.map((model: any) => (
                        <SelectItem key={model} value={model}>
                          {model}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="mm-state" className="text-sm text-gray-600 mb-1.5 block">
                  State
                </label>
                <Select
                  onValueChange={(e) => {
                    searchRequestStore.changeSearchRequest({ state: e });
                  }}
                >
                  <SelectTrigger
                    id="mm-state"
                    className={`${inputSurfaceClass} ${searchRequest.state ? completedFieldClass : ''}`}
                  >
                    <SelectValue placeholder="Registration state" />
                  </SelectTrigger>
                  <SelectContent>
                    {states.map((state) => (
                      <SelectItem key={state} value={state.toLowerCase()}>
                        {state}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label htmlFor="mm-mileage" className="text-sm text-gray-600 mb-1.5 block">
                  Mileage
                </label>
                <Input
                  id="mm-mileage"
                  placeholder="Miles"
                  type="number"
                  className={`${inputSurfaceClass} ${searchRequest.mileage ? completedFieldClass : ''}`}
                  onChange={(e) => {
                    searchRequestStore.changeSearchRequest({
                      mileage: e.target.value,
                    });
                  }}
                />
              </div>
            </div>
            <div className="flex flex-col md:flex-row md:justify-end gap-3 mt-6">
              {renderFreshVehicleOptionsHint(isMakeModelComplete && isSubmitWaitingForFreshVehicleOptions)}
              <Button
                type="button"
                onClick={() => aiAnalysisStore.changeShowAnalysis(true)}
                className="bg-white text-[#00B67A] hover:bg-gray-100 px-6 border border-[#00B67A]"
                size="lg"
                disabled={
                  !searchRequest.year ||
                  !searchRequest.make ||
                  !searchRequest.model ||
                  !searchRequest.state ||
                  !searchRequest.mileage ||
                  isMakeModelLoading ||
                  isSubmitWaitingForFreshVehicleOptions
                }
              >
                <Bot className="w-4 h-4 mr-2" />
                AI Analysis
              </Button>
              <Button
                className="bg-[#00B67A] hover:bg-[#00A067] px-8 max-md:w-full"
                size="lg"
                disabled={
                  !searchRequest.year ||
                  !searchRequest.make ||
                  !searchRequest.model ||
                  !searchRequest.state ||
                  !searchRequest.mileage ||
                  isMakeModelLoading ||
                  isSubmitWaitingForFreshVehicleOptions
                }
                onClick={() => getMyPlansClick({ vin: '', licensePlate: '' })}
              >
                Get My Plans
              </Button>
            </div>
          </TabsContent>

          {/* VIN SEARCH */}
          <TabsContent
            value={SEARCH_TYPES.VIN}
            className={`m-0 space-y-4 data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:slide-in-from-bottom-1 ${alternateLookupPanelClass}`}
          >
            <div>
              <label htmlFor="vin" className="text-sm text-gray-600 mb-1.5 block">
                VIN
              </label>
              <Input
                id="vin"
                placeholder="Car VIN number"
                disabled={isAiAnalysisLoading}
                className={`${inputSurfaceClass} ${searchRequest.vin ? completedFieldClass : ''}`}
                onChange={(e) => {
                  searchRequestStore.changeSearchRequest({
                    vin: e.target.value,
                  });
                }}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="state" className="text-sm text-gray-600 mb-1.5 block">
                  State
                </label>
                <Select
                  onValueChange={(e) => {
                    searchRequestStore.changeSearchRequest({ state: e });
                  }}
                >
                  <SelectTrigger
                    id="state"
                    className={`${inputSurfaceClass} ${searchRequest.state ? completedFieldClass : ''}`}
                  >
                    <SelectValue placeholder="Registration state" />
                  </SelectTrigger>
                  <SelectContent>
                    {states.map((state) => (
                      <SelectItem key={state} value={state.toLowerCase()}>
                        {state}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label htmlFor="mileage" className="text-sm text-gray-600 mb-1.5 block">
                  Mileage
                </label>
                <Input
                  id="mileage"
                  placeholder="Miles"
                  type="number"
                  className={`${inputSurfaceClass} ${searchRequest.mileage ? completedFieldClass : ''}`}
                  onChange={(e) => {
                    searchRequestStore.changeSearchRequest({
                      mileage: e.target.value,
                    });
                  }}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <Button
                type="button"
                onClick={() => aiAnalysisStore.changeShowAnalysis(true)}
                className="bg-white text-[#00B67A] hover:bg-gray-100 px-6 border border-[#00B67A]"
                size="lg"
                disabled={!searchRequest.vin || !searchRequest.state || !searchRequest.mileage || isAiAnalysisLoading}
              >
                <Bot className="w-4 h-4 mr-2" />
                AI Analysis
              </Button>
              <Button
                className="bg-[#00B67A] hover:bg-[#00A067] px-8 max-md:w-full"
                size="lg"
                disabled={!searchRequest.vin || !searchRequest.state || !searchRequest.mileage || isAiAnalysisLoading}
                onClick={() => getMyPlansClick({ make: '', model: '', year: '', licensePlate: '' })}
              >
                Get My Plans
              </Button>
            </div>
          </TabsContent>

          {/* LICENSE PLATE SEARCH */}
          <TabsContent
            value={SEARCH_TYPES.LICENSEPLATE}
            className={`m-0 space-y-4 data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:slide-in-from-bottom-1 ${alternateLookupPanelClass}`}
          >
            <div>
              <label htmlFor="license" className="text-sm text-gray-600 mb-1.5 block">
                License Plate
              </label>
              <Input
                id="license"
                placeholder="License Plate"
                disabled={isAiAnalysisLoading}
                className={`${inputSurfaceClass} ${searchRequest.licensePlate ? completedFieldClass : ''}`}
                onChange={(e) => {
                  searchRequestStore.changeSearchRequest({
                    licensePlate: e.target.value,
                  });
                }}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="lp-state" className="text-sm text-gray-600 mb-1.5 block">
                  State
                </label>
                <Select
                  onValueChange={(e) => {
                    searchRequestStore.changeSearchRequest({ state: e });
                  }}
                >
                  <SelectTrigger
                    id="lp-state"
                    className={`${inputSurfaceClass} ${searchRequest.state ? completedFieldClass : ''}`}
                  >
                    <SelectValue placeholder="Registration state" />
                  </SelectTrigger>
                  <SelectContent>
                    {states.map((state) => (
                      <SelectItem key={state} value={state.toLowerCase()}>
                        {state}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label htmlFor="lp-mileage" className="text-sm text-gray-600 mb-1.5 block">
                  Mileage
                </label>
                <Input
                  id="lp-mileage"
                  placeholder="Miles"
                  type="number"
                  className={`${inputSurfaceClass} ${searchRequest.mileage ? completedFieldClass : ''}`}
                  onChange={(e) => {
                    searchRequestStore.changeSearchRequest({
                      mileage: e.target.value,
                    });
                  }}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <Button
                type="button"
                onClick={() => aiAnalysisStore.changeShowAnalysis(true)}
                className="bg-white text-[#00B67A] hover:bg-gray-100 px-6 border border-[#00B67A]"
                size="lg"
                disabled={
                  !searchRequest.licensePlate || !searchRequest.state || !searchRequest.mileage || isAiAnalysisLoading
                }
              >
                <Bot className="w-4 h-4 mr-2" />
                AI Analysis
              </Button>
              <Button
                className="bg-[#00B67A] hover:bg-[#00A067] px-8 max-md:w-full"
                size="lg"
                disabled={
                  !searchRequest.licensePlate || !searchRequest.state || !searchRequest.mileage || isAiAnalysisLoading
                }
                onClick={() => getMyPlansClick({ make: '', model: '', year: '', vin: '' })}
              >
                Get My Plans
              </Button>
            </div>
          </TabsContent>
        </div>
      </Tabs>

      {showAnalysis ? <AiVehicleAnalysis /> : null}
    </div>
  );
}
